import { v4 as uuidv4 } from 'uuid';
import type {
  AgentMode,
  AgentTreeView,
  ChatElement,
  ContextUsage,
  ErrorMessage,
  InfoMessage,
  Message,
  RetryStatus,
  SkillConflictView,
  SkillConflictsMessage,
  ToolCall,
  ToolInput,
} from '@gitlab-org/tui';
import type { NodeEvent } from '@gitlab-lsp/workflow-api';
import { Logger, withPrefix } from '@gitlab-org/logging';
import {
  AgentEvent,
  AgentEventType,
  CliBackend,
  EntryAttribution,
  RetryAgentEvent,
  SessionEvent,
  UserAction,
  UserEvent,
  UserEventType,
} from '../backend/backend';
import { AgentTree, ROOT_NODE_ID, type DelegationSignal } from './agent_tree';

export interface Session {
  readonly sessionId: string;
  /** Every element of the session, parent and sub-agent alike, in arrival order. */
  readonly elements: readonly ChatElement[];
  /** Undefined for a session with no sub-agents. */
  readonly agentTree: AgentTreeView | undefined;
  /** Elements of the agent being viewed. Equals {@link elements} without sub-agents. */
  readonly viewElements: readonly ChatElement[];
  readonly isLoading: boolean;
  readonly retryStatus: RetryStatus | undefined;
  readonly contextUsage: ContextUsage | undefined;
  /** Accumulated across checkpoints. Nothing consumes it yet. */
  readonly nodeEvents: readonly NodeEvent[];

  preinitialize(): Promise<void>;
  /** True when the element belongs to the root agent rather than a sub-agent's sub-session. */
  isRootElement(elementId: string): boolean;
  /** The agent that owns the element; displayName absent for the root. */
  agentForElement(elementId: string): { nodeId: string; displayName?: string };
  rehydrateFromEvents(events: SessionEvent[]): void;
  addUserMessageAndStartLoading(content: string, agentMode?: AgentMode): void;
  addError(message: string, timestamp?: number): ErrorMessage;
  addInfo(message: string): InfoMessage;
  addSkillConflicts(conflicts: SkillConflictView[]): SkillConflictsMessage;
  sendMessageStream(action: UserAction): AsyncGenerator<ChatElement | RetryAgentEvent>;
  cancelStream(): void;
  enterSubagentView(childNumber?: number): boolean;
  exitSubagentView(): boolean;
}

export class ChatSession implements Session {
  #initialSessionId: string;

  #backend: CliBackend;

  #logger: Logger;

  #elements: ChatElement[] = [];

  #isLoading = false;

  /**
   * Transient retry progress for the in-flight turn. Set when a Retry event
   * arrives, cleared by any subsequent real event and at turn finalization.
   * Never persisted to #elements and never set during rehydration.
   */
  #retryStatus?: RetryStatus;

  /** Latest context-window usage reported by the backend. */
  #contextUsage?: ContextUsage;

  #abortController?: AbortController;

  #currentAssistantMessage?: Message;

  #toolCalls = new Map<string, ToolCall>();

  #agentTree = new AgentTree();

  #appliedDelegationMarkers = new Set<string>();

  #nodeEvents: NodeEvent[] = [];

  constructor(sessionId: string, backend: CliBackend, logger: Logger) {
    this.#initialSessionId = sessionId;
    this.#backend = backend;
    this.#logger = withPrefix(logger, `[Session: ${sessionId}]`);
  }

  /**
   * Prefers the backend's current id, falling back to the id passed at
   * construction when the backend does not expose one.
   */
  get sessionId(): string {
    return this.#backend.getSessionId?.() ?? this.#initialSessionId;
  }

  get elements(): readonly ChatElement[] {
    return this.#elements;
  }

  get agentTree(): AgentTreeView | undefined {
    if (!this.#agentTree.hasSubagents) return undefined;
    return this.#agentTree.toView(this.#elements);
  }

  get viewElements(): readonly ChatElement[] {
    return this.#agentTree.activeNodeElements(this.#elements);
  }

  enterSubagentView(childNumber?: number): boolean {
    if (childNumber === undefined) return this.#agentTree.enterActiveChild();
    return this.#agentTree.enterChild(childNumber);
  }

  exitSubagentView(): boolean {
    return this.#agentTree.exitToParent();
  }

  isRootElement(elementId: string): boolean {
    return this.#agentTree.nodeIdForElement(elementId) === ROOT_NODE_ID;
  }

  agentForElement(elementId: string): { nodeId: string; displayName?: string } {
    return this.#agentTree.agentForElement(elementId);
  }

  get nodeEvents(): readonly NodeEvent[] {
    return this.#nodeEvents;
  }

  get isLoading(): boolean {
    return this.#isLoading;
  }

  get retryStatus(): RetryStatus | undefined {
    return this.#retryStatus;
  }

  get contextUsage(): ContextUsage | undefined {
    return this.#contextUsage;
  }

  rehydrateFromEvents(events: SessionEvent[]): void {
    this.#elements = [];
    this.#agentTree.reset();
    this.#appliedDelegationMarkers.clear();
    this.#nodeEvents = [];
    for (const event of events) {
      this.#drainGenerator(this.#processSessionEvent(event));
    }
    this.#elements = this.#elements.map((el) =>
      el.type === 'message' ? { ...el, isComplete: true } : el,
    );
    // A delegation the exit interrupted replays without its closing marker, so it
    // would resurrect as running even though no stream is alive to ever close it.
    this.#settleInterruptedDelegations();
    this.#finalizeTurn();
  }

  addUserMessageAndStartLoading(content: string, agentMode?: AgentMode): void {
    const message: Message = {
      id: uuidv4(),
      type: 'message',
      role: 'user',
      content,
      timestamp: Date.now(),
      isComplete: true,
      agentMode,
    };
    this.#elements = [...this.#elements, message];
    this.#agentTree.route(message.id);
    this.#isLoading = true;
  }

  addError(message: string, timestamp?: number): ErrorMessage {
    const errorMessage: ErrorMessage = {
      id: `error-${uuidv4()}`,
      type: 'error',
      error: message,
      timestamp: timestamp ?? Date.now(),
    };
    this.#elements = [...this.#elements, errorMessage];
    this.#agentTree.route(errorMessage.id);
    return errorMessage;
  }

  addInfo(message: string): InfoMessage {
    const infoMessage: InfoMessage = {
      id: `info-${uuidv4()}`,
      type: 'info',
      message,
      timestamp: Date.now(),
    };
    this.#elements = [...this.#elements, infoMessage];
    this.#agentTree.route(infoMessage.id);
    return infoMessage;
  }

  addSkillConflicts(conflicts: SkillConflictView[]): SkillConflictsMessage {
    const message: SkillConflictsMessage = {
      id: `skill-conflicts-${uuidv4()}`,
      type: 'skill_conflicts',
      conflicts,
      timestamp: Date.now(),
    };
    this.#elements = [...this.#elements, message];
    this.#agentTree.route(message.id);
    return message;
  }

  async *sendMessageStream(action: UserAction): AsyncGenerator<ChatElement | RetryAgentEvent> {
    this.#isLoading = true;
    this.#abortController = new AbortController();

    try {
      const agentEvents = this.#backend.sendMessageStream(action, this.#abortController.signal);
      const chatElements = this.#processAgentEvents(agentEvents);
      const chatElementsWithCompletion = this.#withCompletionTracking(chatElements);

      yield* chatElementsWithCompletion;
    } catch (error) {
      this.#logger.error('Error in message streaming:', error);
      yield this.addError('Sorry, there was an error processing your message. Please try again.');
    } finally {
      this.#finalizeTurn();
    }
  }

  async *#processAgentEvents(
    events: AsyncIterable<AgentEvent>,
  ): AsyncGenerator<ChatElement | RetryAgentEvent> {
    for await (const event of events) {
      yield* this.#processAgentEvent(event);
    }
  }

  cancelStream(): void {
    this.#abortController?.abort();
  }

  async preinitialize(): Promise<void> {
    await this.#backend.preinitialize?.();
  }

  *#processSessionEvent(event: SessionEvent): Generator<ChatElement | RetryAgentEvent> {
    if (event.type === UserEventType.UserMessage) {
      yield* this.#processUserEvent(event);
    } else {
      yield* this.#processAgentEvent(event);
    }
  }

  *#processUserEvent(event: UserEvent): Generator<ChatElement> {
    const userMessage: Message = {
      id: event.messageId,
      type: 'message',
      role: 'user',
      content: event.content,
      timestamp: event.timestamp,
      isComplete: true,
    };
    this.#elements = [...this.#elements, userMessage];
    this.#agentTree.route(userMessage.id);
    yield userMessage;
  }

  *#processAgentEvent(event: AgentEvent): Generator<ChatElement | RetryAgentEvent> {
    // Retry status is transient: a Retry event sets it; any other event clears
    // it once the stream resumes. A retry produces no ChatElement; instead the
    // retry event is yielded onward so the UI can re-sync from the session.
    if (event.type === AgentEventType.Retry) {
      this.#retryStatus = {
        attempt: event.attempt,
        maxAttempts: event.maxAttempts,
        backoffMs: event.backoffMs,
        startedAt: Date.now(),
      };
      yield event;
      return;
    }
    // Context usage is a side-channel update: record the latest counts (they
    // persist across turns) and produce no ChatElement. It is not a "real"
    // turn event, so it does not clear the transient retry status.
    if (event.type === AgentEventType.TokenUsage) {
      this.#contextUsage = { totalTokens: event.totalTokens, maxTokens: event.maxTokens };
      return;
    }
    if (event.type === AgentEventType.Delegation) {
      if (!this.#appliedDelegationMarkers.has(event.markerId)) {
        this.#appliedDelegationMarkers.add(event.markerId);
        const signal: DelegationSignal = {
          phase: event.phase,
          subagentName: event.subagentName,
          subsessionId: event.subsessionId,
          attribution: event.attribution,
        };
        if (!this.#agentTree.applyDelegation(signal)) {
          this.#logger.debug(`Delegation marker matched no sub-session: ${JSON.stringify(signal)}`);
        }
      }
      // Outside the marker dedup: re-sent markers carry the prompt as it streams in.
      if (event.phase === 'started') {
        const prompt = this.#upsertDelegationPrompt(event);
        if (prompt) yield prompt;
      }
      return;
    }
    // Accumulation relies on the backend's incremental contract: each checkpoint carries
    // only the events appended since the last one (see nodeEventsFrom, and the same
    // contract consumed in packages/webview's nodeLifecycle.ts). A cumulative re-send
    // would duplicate entries here — the first consumer must fold idempotently by
    // (run_id, phase), as foldNodeEvents does, rather than trust this list's uniqueness.
    if (event.type === AgentEventType.NodeLifecycle) {
      this.#nodeEvents = [...this.#nodeEvents, ...event.events];
      return;
    }
    // Any subsequent real event clears the transient retry status; the real
    // element from the switch below drives the next sync.
    this.#retryStatus = undefined;

    switch (event.type) {
      case AgentEventType.TextChunk: {
        if (
          !this.#currentAssistantMessage ||
          this.#currentAssistantMessage.id !== event.messageId
        ) {
          this.#currentAssistantMessage = {
            id: event.messageId,
            type: 'message',
            role: 'assistant',
            content: event.content,
            timestamp: event.timestamp,
            isComplete: false,
          };
          // A resumed session rehydrates history into #elements before streaming;
          // if a checkpoint re-emits a message whose id is already present, update
          // it in place rather than appending a duplicate. An unchanged re-emit of
          // a complete message is left untouched: reopening it (isComplete: false)
          // would shrink the TUI's frozen region and reprint the tail of the chat
          // log into the terminal scrollback.
          const existing = this.#elements.find((el) => el.id === event.messageId);
          if (!existing) {
            this.#elements = [...this.#elements, this.#currentAssistantMessage];
          } else if (
            existing.type !== 'message' ||
            !existing.isComplete ||
            existing.content !== event.content
          ) {
            this.#updateElement(this.#currentAssistantMessage);
          }
        } else {
          this.#currentAssistantMessage.content += event.content;
          this.#updateElement(this.#currentAssistantMessage);
        }
        this.#routeAttribution(event.messageId, event.attribution);
        yield { ...this.#currentAssistantMessage };
        break;
      }

      case AgentEventType.ToolStart: {
        const toolCall: ToolCall = {
          id: event.toolId,
          type: 'tool',
          name: event.name,
          input: event.input as ToolInput,
          state: { type: 'loading' },
          timestamp: event.timestamp,
        };
        this.#toolCalls.set(event.toolId, toolCall);
        this.#createOrUpdateToolCall(toolCall);
        this.#routeAttribution(event.toolId, event.attribution);
        yield toolCall;
        break;
      }

      case AgentEventType.ToolAwaitingApproval: {
        const toolCall: ToolCall = {
          id: event.toolId,
          type: 'tool',
          name: event.toolName,
          input: event.input as ToolInput,
          state: {
            type: 'approval_request',
            content: '',
            availableScopes: event.availableScopes,
            suggestedPatterns: event.suggestedPatterns,
          },
          timestamp: event.timestamp,
        };
        this.#toolCalls.set(event.toolId, toolCall);
        this.#createOrUpdateToolCall(toolCall);
        this.#routeApprovalAttribution(event.toolId, event.attribution);
        yield toolCall;
        break;
      }

      case AgentEventType.ToolComplete: {
        // Look up the existing tool element by id. We check #toolCalls first (fast
        // path for the same-stream case) but fall back to #elements so that tools
        // which were registered in a previous stream (e.g. after an approval
        // round-trip where #finalizeTurn cleared #toolCalls) are still updated.
        const existing =
          this.#toolCalls.get(event.toolId) ??
          (this.#elements.find(
            (el): el is ToolCall => el.type === 'tool' && el.id === event.toolId,
          ) as ToolCall | undefined);
        if (existing) {
          const updatedToolCall: ToolCall = {
            ...existing,
            ...(event.name !== undefined ? { name: event.name } : {}),
            ...(event.input !== undefined ? { input: event.input as ToolInput } : {}),
            state: event.error
              ? { type: 'error', error: event.error }
              : { type: 'success', output: event.result },
          };
          this.#createOrUpdateToolCall(updatedToolCall);
          this.#routeAttribution(event.toolId, event.attribution);
          this.#toolCalls.delete(event.toolId);
          yield updatedToolCall;
        }
        break;
      }

      case AgentEventType.Error: {
        this.#logger.error(`Agent error: ${event.message}`);
        yield this.addError(event.message, event.timestamp);
        break;
      }

      default:
        this.#logger.warn(`Unknown agent event type: ${JSON.stringify(event)}`);
        break;
    }
  }

  async *#withCompletionTracking(
    stream: AsyncGenerator<ChatElement | RetryAgentEvent>,
  ): AsyncGenerator<ChatElement | RetryAgentEvent> {
    let trackingMessage: Message | undefined;

    for await (const element of stream) {
      if (element.type === AgentEventType.Retry) {
        yield element;
        // eslint-disable-next-line no-continue
        continue;
      }

      if (trackingMessage && this.#shouldCompleteTrackedMessage(element, trackingMessage)) {
        this.#markComplete(trackingMessage.id);
        yield { ...trackingMessage, isComplete: true };
        trackingMessage = undefined;
      }

      if (this.#isStreamingAssistantMessage(element)) {
        trackingMessage = element;
      }

      yield element;
    }

    if (trackingMessage) {
      this.#markComplete(trackingMessage.id);
      yield { ...trackingMessage, isComplete: true };
    }
  }

  #markComplete(id: string | undefined): void {
    if (!id) return;
    this.#elements = this.#elements.map((el) => (el.id === id ? { ...el, isComplete: true } : el));
  }

  #createOrUpdateToolCall(toolCall: ToolCall): void {
    const index = this.#elements.findIndex((el) => el.type === 'tool' && el.id === toolCall.id);
    if (index < 0) {
      this.#elements = [...this.#elements, toolCall];
      return;
    }
    const updatedElements = [...this.#elements];
    updatedElements[index] = toolCall;
    this.#elements = updatedElements;
  }

  /** Attribution-less updates are missing information, so they never move an element. */
  #routeAttribution(elementId: string, attribution?: EntryAttribution): void {
    if (!attribution && this.#agentTree.isRouted(elementId)) return;
    this.#agentTree.route(elementId, attribution);
  }

  /**
   * Approval-request entries can arrive without attribution. Delegation is
   * strictly sequential, so a request arriving while exactly one sub-session
   * is open belongs to that sub-agent; anything ambiguous stays with the root.
   */
  #routeApprovalAttribution(toolId: string, attribution?: EntryAttribution): void {
    if (attribution) {
      this.#agentTree.route(toolId, attribution);
      return;
    }
    // An earlier attributed event (e.g. ToolStart) already placed it on a sub-session.
    if (this.#agentTree.nodeIdForElement(toolId) !== ROOT_NODE_ID) return;
    const soleOpenNodeId = this.#agentTree.soleOpenSubsessionNodeId;
    if (soleOpenNodeId !== undefined) {
      this.#agentTree.routeToNode(toolId, soleOpenNodeId);
      return;
    }
    this.#routeAttribution(toolId, undefined);
  }

  #updateElement(element: ChatElement): void {
    const index = this.#elements.findIndex((el) => el.id === element.id);
    if (index >= 0) {
      this.#elements = [
        ...this.#elements.slice(0, index),
        element,
        ...this.#elements.slice(index + 1),
      ];
    }
  }

  #upsertDelegationPrompt(event: {
    subagentName?: string;
    subsessionId?: string;
    prompt?: string;
    timestamp: number;
  }): Message | undefined {
    const nodeId = this.#agentTree.subsessionNodeId(event.subagentName, event.subsessionId);
    if (nodeId === undefined || event.prompt === undefined) return undefined;

    const id = `${nodeId}:prompt`;
    const existing = this.#elements.find((el) => el.id === id);
    if (existing?.type === 'message' && existing.content === event.prompt) return undefined;

    const message: Message = {
      id,
      type: 'message',
      role: 'user',
      content: event.prompt,
      timestamp: event.timestamp,
      isComplete: false,
    };
    if (existing) {
      this.#updateElement(message);
    } else {
      this.#elements = [...this.#elements, message];
    }
    this.#agentTree.routeToNode(id, nodeId);
    return message;
  }

  #finalizeTurn(): void {
    // Cancellation emits no closing delegation markers (#2780), so settle them here.
    if (this.#abortController?.signal.aborted) this.#settleInterruptedDelegations();
    // Only delegation prompts are ever created incomplete among user messages.
    this.#elements = this.#elements.map((el) =>
      el.type === 'message' && el.role === 'user' && !el.isComplete
        ? { ...el, isComplete: true }
        : el,
    );
    this.#isLoading = false;
    this.#retryStatus = undefined;
    this.#currentAssistantMessage = undefined;
    this.#toolCalls.clear();
    this.#abortController = undefined;
  }

  /** For turns that ended without closing markers: cancellation and killed-then-resumed runs. */
  #settleInterruptedDelegations(): void {
    const lastElement = this.#elements[this.#elements.length - 1];
    const waitingNodeId =
      lastElement?.type === 'tool' && lastElement.state.type === 'approval_request'
        ? this.#agentTree.nodeIdForElement(lastElement.id)
        : undefined;
    const parked = this.#agentTree.settleOpenSubsessions('cancelled', waitingNodeId);
    // The parked delegation's card keeps loading: answering the prompt resumes it.
    if (parked) return;
    this.#elements = this.#elements.map((el) =>
      el.type === 'tool' && el.input.tool === 'delegate_task' && el.state.type === 'loading'
        ? { ...el, state: { type: 'error' as const, error: 'Cancelled' } }
        : el,
    );
  }

  #drainGenerator(gen: Generator<unknown>): void {
    let result = gen.next();
    while (!result.done) {
      result = gen.next();
    }
  }

  #isStreamingAssistantMessage(el: ChatElement): el is Message {
    return el.type === 'message' && el.role === 'assistant' && !el.isComplete;
  }

  #shouldCompleteTrackedMessage(el: ChatElement, tracked: Message): boolean {
    return el.type !== 'message' || el.id !== tracked.id;
  }
}
